<!-- sections/WidgetTitleSection.vue -->
<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import { InputType } from '@/interfaces/InputFields';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import ConnxEditor from '@/components/Molecules/Editor/ConnxEditor.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import type { DashboardWidget } from '@/interfaces/Dashboard';

interface Props {
  widget: DashboardWidget;
  errors?: Map<string, string | null>;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
  'toggle-help-expanded': [expanded: boolean];
  'update:title': [title: string];
  'update:subtitle': [subtitle: string];
  'update:help-field-text': [helpText: string];
  'clone-help-text': [];
  'reset-help-text': [];
}>();

const helpInput = ref<InstanceType<typeof ConnxField>>();
const helpFieldTextExpanded = ref(false);

const isEditingHelpFieldText = computed(() => {
  return props.widget.defaultJsonProperties.helpFieldText !== undefined;
});

const helpFieldText = computed<string>({
  get: () => {
    return props.widget.defaultJsonProperties?.helpFieldText ?? props.widget.helpFieldText!;
  },
  set: (val: string) => {
    emit('update:help-field-text', val);
  },
});

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};

const toggleHelpExpanded = (expanded: boolean) => {
  helpFieldTextExpanded.value = expanded;
  emit('toggle-help-expanded', expanded);
};

const cloneHelpfieldText = async () => {
  emit('clone-help-text');
  helpFieldTextExpanded.value = true;
  await nextTick();
  helpInput.value?.focus();
};

const resetHelpText = () => {
  helpFieldTextExpanded.value = false;
  emit('reset-help-text');
};

const handleHelpTextChange = (value: string) => {
  emit('update:help-field-text', value);
};
</script>

<template>
  <BaseSection :should-show="true">
    <connx-field
      name="title"
      :type="InputType.Text"
      :maxlength="100"
      label="Title"
      :model-value="widget.defaultJsonProperties.title"
      @update:model-value="(val) => $emit('update:title', val)"
      @update:error="handleFieldError"
    />

    <connx-field
      name="subheading"
      :type="InputType.Text"
      :maxlength="100"
      label="Subheading"
      :model-value="widget.defaultJsonProperties.subtitle"
      @update:model-value="(val) => $emit('update:subtitle', val)"
      @update:error="handleFieldError"
    />

    <label class="Field__label" for="help">Help Text</label>
    <connx-editor
      :model-value="helpFieldText"
      name="help"
      id="help"
      ref="helpInput"
      @update:model-value="handleHelpTextChange"
      @update:error="handleFieldError"
      :expanded="helpFieldTextExpanded"
      :disabled="!isEditingHelpFieldText"
      @update:expanded="toggleHelpExpanded"
    />

    <div class="tw-flex tw-justify-between">
      <div>
        <connx-button
          button-style="blank"
          unpadded
          @click="toggleHelpExpanded(!helpFieldTextExpanded)"
          class="tw-text-connx-primary tw-text-semibold tw-px-0 tw-py-1"
        >
          {{ helpFieldTextExpanded ? 'Show less' : 'Show more' }}
        </connx-button>
      </div>

      <div
        v-if="!isEditingHelpFieldText && widget.defaultJsonProperties?.helpFieldText === undefined"
        class="tw-flex tw-gap-x-2 tw-items-center"
      >
        This is the default help text.
        <connx-button
          button-style="blank"
          unpadded
          @click="cloneHelpfieldText"
          class="tw-text-connx-primary tw-px-0 tw-py-1"
        >
          Click to edit
        </connx-button>
      </div>

      <connx-button
        v-if="isEditingHelpFieldText"
        button-style="blank"
        unpadded
        @click="resetHelpText"
        class="tw-text-connx-primary"
      >
        Reset to default
      </connx-button>
    </div>
  </BaseSection>
</template>
